import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { Input, InputText } from '@/components/form';
import { FieldValues, useForm } from 'react-hook-form';
import { BadgeTypes, Button } from '@/components/common';
import Icons from '@/const/icons';
import { ConfirmDeclineCard } from './ConfirmDeclineCard';
import { SecurityDocument } from '@/components/screens/retainer/security/SecurityPdf';
import { RetainerDocumentPdf } from '@/components/screens/retainer/pdf';
import { MassActionDocument } from '@/components/screens/retainer/MassActionPdf';
import { CaseType, UploadFile } from '@/interfaces';
import { applicationsService } from '@/api';
import { useDebouncedCallback } from 'use-debounce';
import { getCurrentFormatDate } from '@/utils';
import clsx from 'clsx';
import { useNotification } from '@/hooks';
import { format } from 'date-fns';

interface Props {
	actionRequiredId: string;
	applicationId: number;
	name: string;
	caseName: string;
	firmName: string;
	fullName: string;
	companyName: string;
	companyAlias: string;
	eligibilityCriteria: string;
	requiredDocuments: string;
	optionalLocation: string;
	lawyerSign: string;
	hasNextDocument: boolean;
	categoryInfo: BadgeTypes | undefined;
	userSign: string;
	signedAt: string;
	onContinue: (isApproved: boolean) => void;
	caseType?: CaseType;
}

export const FormRetainer: React.FC<Props> = ({
	applicationId,
	actionRequiredId,
	name,
	caseName,
	firmName,
	fullName,
	companyName,
	companyAlias,
	eligibilityCriteria,
	requiredDocuments,
	optionalLocation,
	lawyerSign,
	categoryInfo,
	hasNextDocument,
	onContinue,
	userSign,
	signedAt,
	caseType,
}) => {
	const { toastError } = useNotification();
	const [isConfirmingDecline, setIsConfirmingDecline] = React.useState(false);
	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { isSubmitting, errors },
	} = useForm({ mode: 'onChange' });

	const rules = {
		userSignature: {
			pattern: {
				value: /^[A-Za-z\s]+$/i,
				message: 'Only alphabetic characters accepted',
			},
			maxLength: {
				value: 65,
				message: 'Your first name has to have less than 65 characters',
			},
			validate: (value: string) =>
				!!value?.trim() || 'Whitespace only is not allowed',
		},
	};

	const userSignature = watch('userSignature');
	const isCompleted = !!userSign && !!signedAt;

	React.useEffect(() => {
		if (userSign && signedAt) {
			const formattedDate = format(signedAt, 'MM/dd/yyyy');
			setValue('userSignature', userSign);
			setValue('date', formattedDate);
		}
	}, [userSign, signedAt]);

	const setTimeStamp = useDebouncedCallback(() => {
		const formattedDate = getCurrentFormatDate();
		setValue('date', formattedDate);
	}, 1000);

	const createRetainerPDF = async (
		data: FieldValues
	): Promise<Blob | undefined> => {
		try {
			const formData = {
				name: name,
				caseName: caseName,
				firmName: firmName,
				fullName: fullName,
				companyName: companyName ?? '',
				companyAlias: companyAlias ?? '',
				eligibilityCriteria: eligibilityCriteria ?? '',
				requiredDocuments: requiredDocuments ?? '',
				optionalLocation: optionalLocation,
				lawyerSign: lawyerSign ?? '',
				userSign: data.userSignature ?? '',
				signedAt: data.date ?? '',
			};

			const document =
				caseType === 'MASS_ACTION' ? (
					<MassActionDocument
						name={formData.name}
						fullName={formData.fullName}
						lawyerSign={formData.lawyerSign}
						userSign={formData.userSign}
						signedAt={formData.signedAt}
						categoryInfo={categoryInfo}
						caseName={caseName}
					/>
				) : categoryInfo?.toLowerCase() === 'securities' ? (
					<SecurityDocument
						name={formData.name}
						fullName={formData.fullName}
						lawyerSign={formData.lawyerSign}
						userSign={formData.userSign}
						signedAt={formData.signedAt}
					/>
				) : (
					<RetainerDocumentPdf data={formData} />
				);

			const pdfBlob = await pdf(document).toBlob();
			const file = new File([pdfBlob], 'Retainer.pdf', {
				type: 'application/pdf',
			});

			return file;
		} catch (error) {
			console.log('ERROR upload', error);
		}
	};

	const onSubmit = async (data: FieldValues, isApproved: boolean) => {
		try {
			if (isCompleted) {
				onContinue(true);
				return;
			}

			const file = await createRetainerPDF(data);

			if (!file) return;

			const newData: UploadFile = {
				doc: file,
				applicationId: Number(applicationId),
				actionRequiredId: Number(actionRequiredId),
				sign: data.userSignature?.trim() || '',
				fileName: 'Retainer',
				is_approved: String(isApproved),
			};

			await applicationsService.postSignUploadUser(newData);
			onContinue(isApproved);
		} catch (error) {
			toastError('An error occurred, please try again.');
		}
	};

	return (
		<form className="flex flex-col flex-1 lg:min-h-0 relative">
			<div className="flex-1 space-y-4">
				<InputText
					disabled={isCompleted}
					name="userSignature"
					register={register}
					placeholder="/s/"
					classNameInput={clsx('disabled:!bg-gray-5', {
						'!font-praise !text-2xl': !!userSignature,
					})}
					onChange={({ target }) => {
						setTimeStamp();
						setValue('userSignature', target.value);
					}}
					rules={rules.userSignature}
					error={errors?.userSignature}
				/>
				<Input
					title="Date"
					name="date"
					register={register}
					disabled
					className="w-full"
					classNameInput="disabled:!bg-[#F4F4F4A3]  !h-12"
					placeholder="MM/DD/YYY"
				/>
			</div>
			<div className="relative flex items-center gap-4 border-t pt-3 justify-end">
				<Button
					size="fit"
					label="Decline"
					type="button"
					decoration="danger-outline"
					disabled={isCompleted || isSubmitting}
					className="!w-auto lg:!w-fit"
					onClick={() => setIsConfirmingDecline(true)}
				/>
				<Button
					iconRight={hasNextDocument}
					icon={Icons.arrowRight}
					disabled={
						isCompleted ? false : !userSignature?.trim() || isSubmitting
					}
					decoration="primary"
					className="w-[106px] md:w-fit !h-12 !p-0 lg:!px-6 lg:!py-[14px]"
					classNameButton="!w-4 hidden lg:block"
					onClick={handleSubmit((data) => onSubmit(data, true))}
					label={hasNextDocument ? 'Agree & Continue' : 'Complete Singing'}
				/>
				{isConfirmingDecline && (
					<div className="absolute right-0 bottom-0">
						<ConfirmDeclineCard
							isSubmitting={isSubmitting}
							onCancel={() => setIsConfirmingDecline(false)}
							onConfirm={handleSubmit((data) => onSubmit(data, false))}
						/>
					</div>
				)}
			</div>
		</form>
	);
};
